'use client';

import { c as _c } from "react/compiler-runtime";
import Link from '@/components/ui/Link';
import { PageJSON, PageHeading, BreadcrumbConfig, BreadcrumbItem, Section, Column, Row, TransformedNewsItem } from '@/lib/types/pageBuilder';
import { useTheme } from 'next-themes';
import RowRenderer from './RowRenderer';
import { DynamicTabInfo } from './TabBarSection';
import type { IncludeSectionApiData } from '@/lib/pageUtils';
import { normalizeApiEndpoint } from '@/lib/apiUtils';
import LazyRow from '@/components/ui/LazyRow';
import { useTabBreadcrumbs } from '@/contexts/TabBreadcrumbContext';

/** Number of rows to render immediately (above-the-fold) */
const PRIORITY_ROWS_COUNT = 1;
interface PageRendererProps {
  pageData: PageJSON;
  collectionsData: Map<string, TransformedNewsItem[]>;
  dynamicTabsMap?: Map<string, DynamicTabInfo[]>;
  includeSectionsData?: Map<string, Section>;
  includeSectionApiData?: IncludeSectionApiData;
  /** Data for page API bindings, keyed by binding ID */
  pageBindingsData?: Map<string, Record<string, unknown>>;
  /** API data for pageHeading when sourceType is 'dynamic' with queryParams */
  headingApiData?: Record<string, unknown>;
  /** Data from double API calls (keyed by binding ID) - used when double_api_enabled is true */
  doubleApiData?: Map<string, Record<string, unknown>>;
}

/**
 * Get nested field value from object using dot notation path
 * Supports paths like "name", "parent.name", "items[0].story.headline", "tags.tags[].name"
 * Empty brackets [] means first element of array
 */
function getFieldValue(data: Record<string, unknown>, fieldPath: string): string {
  if (!data || !fieldPath) {
    return '';
  }

  // Handle empty brackets [] - replace with [0] for first element
  const normalizedPath = fieldPath.replace(/\[\]/g, '[0]');
  const parts = normalizedPath.replace(/\[(\d+)\]/g, '.$1').split('.');
  let value: unknown = data;
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    const valueType = Array.isArray(value) ? 'array' : typeof value;
    if (value === null || value === undefined) {
      return '';
    }
    if (Array.isArray(value)) {
      const index = parseInt(part, 10);
      if (!isNaN(index)) {
        value = value[index];
      } else {
        value = (value as Record<string, unknown>[])[0]?.[part];
      }
    } else {
      const prevValue = value;
      value = (value as Record<string, unknown>)[part];
      if (value === undefined) {}
    }
  }
  const result = String(value ?? '');
  return result;
}

/**
 * Get the first tab's sectionUrl from page sections
 * Used as fallback URL for breadcrumb items without a configured URL
 */
function getFirstTabSectionUrl(sections: Section[]): string | null {
  // Helper to check columns recursively
  const checkColumns = (columns: Column[]): string | null => {
    for (const col of columns) {
      // Check if column has tabBar
      if (col.tabBar?.tabs) {
        const firstTab = col.tabBar.tabs.find(t => !t.hidden && t.sectionUrl);
        if (firstTab?.sectionUrl) {
          return firstTab.sectionUrl.startsWith('/') ? firstTab.sectionUrl : `/${firstTab.sectionUrl}`;
        }
      }
      // Check nested columns
      if (col.columns) {
        const result = checkColumns(col.columns);
        if (result) return result;
      }
      // Check nested rows
      if (col.rows) {
        for (const nestedRow of col.rows) {
          const result = checkColumns(nestedRow.columns || []);
          if (result) return result;
        }
      }
    }
    return null;
  };
  for (const section of sections) {
    for (const row of section.rows || []) {
      const result = checkColumns(row.columns || []);
      if (result) {
        return result;
      }
    }
  }
  return null;
}

/**
 * Breadcrumb navigation component
 */
function Breadcrumbs({
  config,
  apiData,
  pageBindingsData,
  resolvedHeadingText,
  headingBindingId,
  fallbackUrl
}: {
  config: BreadcrumbConfig;
  apiData?: Record<string, unknown>;
  pageBindingsData?: Map<string, Record<string, unknown>>;
  /** Resolved heading text - used as fallback when breadcrumb uses same binding as heading */
  resolvedHeadingText?: string;
  /** Heading's pageBindingId - to check if breadcrumb should use same text */
  headingBindingId?: string;
  /** Fallback URL for breadcrumb items without a configured URL (from first tab's sectionUrl) */
  fallbackUrl?: string | null;
}) {
  // Get additional breadcrumbs from active tab (when sectionUrlEnabled is true)
  // isMounted prevents hydration flash - breadcrumbs only show after client hydration
  // firstTabSectionUrl from context (set by TabBarSection) - used as secondary fallback
  const {
    tabBreadcrumbs,
    isMounted,
    firstTabSectionUrl
  } = useTabBreadcrumbs();
  if (!config.enabled) return null;
  const {
    resolvedTheme
  } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const separator = config.separator || '/';
  const breadcrumbStyle: React.CSSProperties = {
    fontSize: config.fontSize || '14px',
    color: isDark ? '#FFFFFF' : config.color || '#666666',
    marginBottom: config.showDivider ? '0' : config.marginBottom || '8px',
    fontWeight: config.fontWeight || '500',
    fontFamily: 'Noto Sans Tamil'
  };
  const linkStyle: React.CSSProperties = {
    color: isDark ? '#FFFFFF' : config.color || '#C00',
    fontFamily: 'Noto Sans Tamil'
  };
  const activeStyle: React.CSSProperties = {
    color: isDark ? '#FFFFFF' : config.activeColor || config.color || '#C00'
  };

  // Check if we have tab breadcrumbs - only show after client hydration to prevent flash
  const hasTabBreadcrumbs = isMounted && tabBreadcrumbs && tabBreadcrumbs.length > 0;

  // Build breadcrumb items
  const breadcrumbElements: React.ReactNode[] = [];

  // Home link
  if (config.showHome !== false) {
    breadcrumbElements.push(<Link key="home" href={config.homeUrl || '/'} className="hover:underline" style={linkStyle}>
        {config.homeLabel || 'வீடு'}
      </Link>);
  }

  // Dynamic/static breadcrumb items from page config
  if (config.items && config.items.length > 0) {
    config.items.forEach((item, index) => {
      let label = '';
      let url = '';

      // Priority 1: Use static label/url if provided directly
      if (item.label) {
        label = item.label;
        url = item.url || '';
      }
      // Priority 2: If isStatic flag is true, use static values (even if empty)
      else if (item.isStatic) {
        label = item.label || '';
        url = item.url || '';
      }
      // Priority 3: Use dynamic fields from API data
      else if (apiData && (item.labelField || item.urlField)) {
        label = item.labelField ? getFieldValue(apiData, item.labelField) : '';
        url = item.urlField ? getFieldValue(apiData, item.urlField) : '';
      }
      if (label) {
        // Use fallbackUrl (from page config) or firstTabSectionUrl (from context) when no URL is available
        // Priority: 1. Configured URL, 2. Prop fallbackUrl, 3. Context firstTabSectionUrl
        const effectiveUrl = url || fallbackUrl || (firstTabSectionUrl && isMounted ? firstTabSectionUrl : '');
        breadcrumbElements.push(effectiveUrl ? <Link key={`item-${index}`} href={effectiveUrl} className="hover:underline" style={linkStyle}>
              {label}
            </Link> : <span key={`item-${index}`} style={linkStyle}>
              {label}
            </span>);
      }
    });
  }

  // Get current page label
  let currentLabel = '';
  if (config.showCurrentPage !== false) {
    currentLabel = config.currentPageLabel || '';

    // Get binding ID - support both currentPageBindingId and currentPageSectionBindingId
    const currentBindingId = config.currentPageBindingId || (config as any).currentPageSectionBindingId;

    // Get current page label based on sourceType
    if (!currentLabel) {
      // Support both page_bind and section_bind source types
      const sourceType = config.currentPageSourceType as string;
      if ((sourceType === 'page_bind' || sourceType === 'section_bind') && currentBindingId) {
        // If breadcrumb uses same binding as heading, use the resolved heading text
        if (currentBindingId === headingBindingId && resolvedHeadingText) {
          currentLabel = resolvedHeadingText;
        }
        // Otherwise try to get from pageBindingsData
        else if (pageBindingsData) {
          const bindingData = pageBindingsData.get(currentBindingId);
          if (bindingData) {
            const fieldName = config.currentPageBindingField || 'name';
            currentLabel = getFieldValue(bindingData, fieldName) || (bindingData as any)._sectionName || (bindingData as any)._collectionName || '';
          }
        }
      } else if (config.currentPageSourceType === 'dynamic' && config.currentPageField && apiData) {
        // Dynamic mode: Get from API data (fetched with queryParams) using field path
        const fieldPaths = Array.isArray(config.currentPageField) ? config.currentPageField : [config.currentPageField];

        // Try each field path until we find a value
        for (const fieldPath of fieldPaths) {
          let value = getFieldValue(apiData, fieldPath);

          // If direct path failed and we have items array, try items[].{field}
          if (!value && apiData.items && Array.isArray(apiData.items)) {
            const fieldParts = fieldPath.split('.');
            const innerPath = fieldParts.length > 1 ? fieldParts.slice(1).join('.') : fieldPath;
            const itemsPath = `items[].${innerPath}`;
            value = getFieldValue(apiData, itemsPath);
          }
          if (value) {
            currentLabel = value;
            break;
          }
        }
      } else if (config.currentPageField && apiData) {
        // Legacy: Get from API data using field path
        const fieldPaths_0 = Array.isArray(config.currentPageField) ? config.currentPageField : [config.currentPageField];

        // Try each field path until we find a value
        for (const fieldPath_0 of fieldPaths_0) {
          const value_0 = getFieldValue(apiData, fieldPath_0);
          if (value_0) {
            currentLabel = value_0;
            break;
          }
        }
      }
    }
  }

  // When tab breadcrumbs exist: show current page as LINK, then tab breadcrumbs as active text
  // When no tab breadcrumbs: show current page as active text
  if (hasTabBreadcrumbs) {
    // Add current page as a link (navigates to first tab's sectionUrl)
    if (currentLabel) {
      // Use fallbackUrl (from page sections) or firstTabSectionUrl (from context) for navigation
      const currentPageUrl = fallbackUrl || (firstTabSectionUrl && isMounted ? firstTabSectionUrl : '');
      breadcrumbElements.push(currentPageUrl ? <Link key="current" href={currentPageUrl} className="hover:underline" style={linkStyle}>
            {currentLabel}
          </Link> : <span key="current" style={linkStyle}>
            {currentLabel}
          </span>);
    }

    // Add tab breadcrumbs at the end as active text
    tabBreadcrumbs.forEach((item_0, index_0) => {
      let label_0 = '';
      if (item_0.label) {
        label_0 = item_0.label;
      } else if (item_0.isStatic) {
        label_0 = item_0.label || '';
      } else if (apiData && item_0.labelField) {
        label_0 = getFieldValue(apiData, item_0.labelField);
      }
      if (label_0) {
        // Last tab breadcrumb item is shown as active (no link)
        const isLastItem = index_0 === tabBreadcrumbs.length - 1;
        breadcrumbElements.push(isLastItem ? <span key={`tab-item-${index_0}`} style={activeStyle}>
              {label_0}
            </span> : item_0.url ? <Link key={`tab-item-${index_0}`} href={item_0.url} className="hover:underline" style={linkStyle}>
                {label_0}
              </Link> : <span key={`tab-item-${index_0}`} style={linkStyle}>
                {label_0}
              </span>);
      }
    });
  } else {
    // No tab breadcrumbs - show current page as active text at the end
    if (currentLabel) {
      breadcrumbElements.push(<span key="current" style={activeStyle}>
          {currentLabel}
        </span>);
    }
  }

  // Divider style
  const dividerStyle: React.CSSProperties = config.showDivider ? {
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: config.dividerColor || '#e5e7eb',
    width: config.dividerWidth || '100%',
    marginTop: config.dividerMarginTop || '8px',
    marginBottom: config.marginBottom || '8px'
  } : {};
  return <>
      <nav className="py-2 px-5" style={breadcrumbStyle}>
        {breadcrumbElements.map((element, index_1) => <span key={index_1}>
            {index_1 > 0 && <span className="mx-1 text-gray-500" style={isDark ? {
          color: 'rgba(255, 255, 255, 0.6)'
        } : {}}>{separator}</span>}
            {element}
          </span>)}
      </nav>
      {config.showDivider && <div style={dividerStyle} />}
    </>;
}

/**
 * Page header with breadcrumb navigation and configurable heading
 */
function PageHeader(t0) {
  const $ = _c(45);
  const {
    heading,
    apiData,
    pageBindingsData,
    doubleApiData,
    sections
  } = t0;
  const {
    resolvedTheme
  } = useTheme();
  const isDark = resolvedTheme === "dark";
  const level = heading.level || 1;
  let bindingId;
  let headingText;
  if ($[0] !== apiData || $[1] !== doubleApiData || $[2] !== heading || $[3] !== pageBindingsData) {
    headingText = heading.text || "";
    bindingId = heading.pageBindingId || (heading as any).sectionBindingId;
    const sourceType = heading.sourceType as string;
    if ((sourceType === "page_bind" || sourceType === "section_bind" || (heading as any).sectionBindingId) && bindingId) {
      const doubleApiBindingData = doubleApiData?.get(bindingId);
      if (doubleApiBindingData) {
        const fieldName = heading.pageBindingField || doubleApiBindingData._responseField as string || "name";
        if (doubleApiBindingData._extractedValue) {
          headingText = String(doubleApiBindingData._extractedValue);
        } else {
          headingText = getFieldValue(doubleApiBindingData, fieldName) || headingText;
        }
      } else {
        if (pageBindingsData) {
          const bindingData = pageBindingsData.get(bindingId);
          if (bindingData) {
            const fieldName_0 = heading.pageBindingField || "name";
            headingText = getFieldValue(bindingData, fieldName_0) || (bindingData as any)._sectionName || (bindingData as any)._collectionName || headingText;
          }
        }
      }
    } else {
      if (heading.sourceType === "dynamic" && heading.field && apiData) {
        let t1;
        if ($[6] !== apiData || $[7] !== heading.field) {
          t1 = getFieldValue(apiData, heading.field);
          $[6] = apiData;
          $[7] = heading.field;
          $[8] = t1;
        } else {
          t1 = $[8];
        }
        let extractedValue = t1;
        if (!extractedValue && apiData.items && Array.isArray(apiData.items)) {
          let t2;
          if ($[9] !== heading.field) {
            const fieldParts = heading.field.split(".");
            t2 = fieldParts.length > 1 ? fieldParts.slice(1).join(".") : heading.field;
            $[9] = heading.field;
            $[10] = t2;
          } else {
            t2 = $[10];
          }
          const innerPath = t2;
          const itemsPath = `items[].${innerPath}`;
          let t3;
          if ($[11] !== apiData || $[12] !== itemsPath) {
            t3 = getFieldValue(apiData, itemsPath);
            $[11] = apiData;
            $[12] = itemsPath;
            $[13] = t3;
          } else {
            t3 = $[13];
          }
          extractedValue = t3;
        }
        headingText = extractedValue || headingText;
      } else {
        if (heading.useField && heading.field && apiData) {
          headingText = getFieldValue(apiData, heading.field) || headingText;
        }
      }
    }
    $[0] = apiData;
    $[1] = doubleApiData;
    $[2] = heading;
    $[3] = pageBindingsData;
    $[4] = bindingId;
    $[5] = headingText;
  } else {
    bindingId = $[4];
    headingText = $[5];
  }
  const t1 = isDark ? "#FFFFFF" : heading.color || "#020202";
  const t2 = heading.align || "left";
  const t3 = heading.fontWeight || "700";
  const t4 = heading.marginTop || "0";
  const t5 = heading.marginBottom || "16px";
  const t6 = heading.padding || "0px 17px";
  let t7;
  if ($[14] !== heading.fontSize) {
    t7 = heading.fontSize ? {
      fontSize: heading.fontSize
    } : {};
    $[14] = heading.fontSize;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t1 || $[17] !== t2 || $[18] !== t3 || $[19] !== t4 || $[20] !== t5 || $[21] !== t6 || $[22] !== t7) {
    t8 = {
      color: t1,
      textAlign: t2,
      fontWeight: t3,
      marginTop: t4,
      marginBottom: t5,
      padding: t6,
      ...t7
    };
    $[16] = t1;
    $[17] = t2;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const headingStyle = t8;
  let t9;
  if ($[24] !== heading.dividerColor || $[25] !== heading.dividerWidth || $[26] !== heading.showDivider) {
    t9 = heading.showDivider ? {
      borderBottomWidth: "1px",
      borderBottomStyle: "solid",
      borderBottomColor: heading.dividerColor || "#E8E8E8",
      width: heading.dividerWidth || "100%"
    } : {};
    $[24] = heading.dividerColor;
    $[25] = heading.dividerWidth;
    $[26] = heading.showDivider;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  const dividerStyle = t9;
  let t10;
  if ($[28] !== headingStyle || $[29] !== headingText || $[30] !== level) {
    t10 = () => {
      if (!headingText) {
        return null;
      }
      const props = {
        style: headingStyle
      };
      switch (level) {
        case 1:
          {
            return <h1 {...props}>{headingText}</h1>;
          }
        case 2:
          {
            return <h2 {...props}>{headingText}</h2>;
          }
        case 3:
          {
            return <h3 {...props}>{headingText}</h3>;
          }
        case 4:
          {
            return <h4 {...props}>{headingText}</h4>;
          }
        case 5:
          {
            return <h5 {...props}>{headingText}</h5>;
          }
        case 6:
          {
            return <h6 {...props}>{headingText}</h6>;
          }
        default:
          {
            return <h1 {...props}>{headingText}</h1>;
          }
      }
    };
    $[28] = headingStyle;
    $[29] = headingText;
    $[30] = level;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  const renderHeading = t10;
  let t11;
  if ($[32] !== apiData || $[33] !== bindingId || $[34] !== heading.breadcrumbs || $[35] !== headingText || $[36] !== pageBindingsData || $[37] !== sections) {
    t11 = heading.breadcrumbs && <Breadcrumbs config={heading.breadcrumbs} apiData={apiData} pageBindingsData={pageBindingsData} resolvedHeadingText={headingText} headingBindingId={bindingId} fallbackUrl={sections ? getFirstTabSectionUrl(sections) : null} />;
    $[32] = apiData;
    $[33] = bindingId;
    $[34] = heading.breadcrumbs;
    $[35] = headingText;
    $[36] = pageBindingsData;
    $[37] = sections;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== renderHeading) {
    t12 = renderHeading();
    $[39] = renderHeading;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== dividerStyle || $[42] !== t11 || $[43] !== t12) {
    t13 = <div style={dividerStyle}>{t11}{t12}</div>;
    $[41] = dividerStyle;
    $[42] = t11;
    $[43] = t12;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  return t13;
}

/**
 * Global index of the first row that actually renders content.
 *
 * `isFirstRow` used to mean "row 0", which is wrong on every page whose layout
 * opens with a breadcrumb / page-heading strip. On `category-page` (the template
 * behind /latest-news and every section URL) row 0 is `colWidths=[12]` with ZERO
 * components, so the LCP grant landed on an empty row and the real hero — a
 * `VerticalBigImageCategoryNewsBG` in row 1 — got no `priority`, no
 * `fetchpriority="high"` and no `<link rel=preload>`. PSI measured the
 * consequence directly: section LCP 4.1s mobile / 2.3s desktop, with the hero
 * arriving several filmstrip frames after first paint, while the only two
 * preloads on the page were the two site logos.
 *
 * A row "renders content" when some column in it (at any nesting depth, incl.
 * tab-bar and slider rows) carries a component with a `component_type`.
 */
function firstContentRowIndex(sections: Section[]): number {
  let globalIndex = 0;
  const columnHasComponent = (column: Column): boolean => {
    if (column?.components?.some(c => c?.component_type)) return true;
    if (column?.columns?.some(columnHasComponent)) return true;
    if (column?.rows?.some(rowHasComponent)) return true;
    if (column?.tabBar?.tabs?.some(t => t?.rows?.some(rowHasComponent))) return true;
    const slider = column?.slider as {
      rows?: Row[];
    } | undefined;
    if (slider?.rows?.some(rowHasComponent)) return true;
    return false;
  };
  const rowHasComponent = (row: Row): boolean => !!row?.columns?.some(columnHasComponent);
  for (const section of sections ?? []) {
    for (const row of section?.rows ?? []) {
      if (rowHasComponent(row)) return globalIndex;
      globalIndex += 1;
    }
  }
  return 0;
}
function SectionRenderer(t0) {
  const $ = _c(19);
  const {
    section,
    collectionsData,
    dynamicTabsMap,
    includeSectionsData,
    includeSectionApiData,
    heroRowIndex: t1,
    globalRowStartIndex: t2
  } = t0;
  const heroRowIndex = t1 === undefined ? 0 : t1;
  const globalRowStartIndex = t2 === undefined ? 0 : t2;
  if (!section || !section.rows || !Array.isArray(section.rows)) {
    return null;
  }
  const t3 = section.id;
  let t4;
  if ($[0] !== collectionsData || $[1] !== dynamicTabsMap || $[2] !== globalRowStartIndex || $[3] !== heroRowIndex || $[4] !== includeSectionApiData || $[5] !== includeSectionsData || $[6] !== section.rows) {
    let t5;
    if ($[8] !== collectionsData || $[9] !== dynamicTabsMap || $[10] !== globalRowStartIndex || $[11] !== heroRowIndex || $[12] !== includeSectionApiData || $[13] !== includeSectionsData || $[14] !== section.rows.length) {
      t5 = (row, index) => {
        const globalRowIndex = globalRowStartIndex + index;
        const isPriorityRow = globalRowIndex < PRIORITY_ROWS_COUNT;
        const rowContent = <RowRenderer key={row.id} row={row} collectionsData={collectionsData} dynamicTabsMap={dynamicTabsMap} includeSectionsData={includeSectionsData} includeSectionApiData={includeSectionApiData} isLastRow={index === section.rows.length - 1} isFirstRow={globalRowIndex === heroRowIndex} />;
        if (!isPriorityRow) {
          return <LazyRow key={row.id} minHeight="300px" rootMargin="400px">{rowContent}</LazyRow>;
        }
        return rowContent;
      };
      $[8] = collectionsData;
      $[9] = dynamicTabsMap;
      $[10] = globalRowStartIndex;
      $[11] = heroRowIndex;
      $[12] = includeSectionApiData;
      $[13] = includeSectionsData;
      $[14] = section.rows.length;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    t4 = section.rows.map(t5);
    $[0] = collectionsData;
    $[1] = dynamicTabsMap;
    $[2] = globalRowStartIndex;
    $[3] = heroRowIndex;
    $[4] = includeSectionApiData;
    $[5] = includeSectionsData;
    $[6] = section.rows;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[16] !== section.id || $[17] !== t4) {
    t5 = <section className="" data-section-id={t3}>{t4}</section>;
    $[16] = section.id;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return t5;
}
export default function PageRenderer(t0) {
  const $ = _c(37);
  const {
    pageData,
    collectionsData,
    dynamicTabsMap,
    includeSectionsData,
    includeSectionApiData,
    pageBindingsData,
    headingApiData: propHeadingApiData,
    doubleApiData
  } = t0;
  if (!pageData || !pageData.sections || !Array.isArray(pageData.sections)) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="page-content border-[0.1px] border-DCDCDC" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let headingApiData = propHeadingApiData;
  if (!headingApiData && pageData.pageHeading?.useField) {
    const {
      collectionId,
      apiEndpoint
    } = pageData.pageHeading;
    let collectionData;
    if (apiEndpoint) {
      let t1;
      if ($[1] !== apiEndpoint || $[2] !== collectionsData) {
        t1 = collectionsData.get(normalizeApiEndpoint(apiEndpoint));
        $[1] = apiEndpoint;
        $[2] = collectionsData;
        $[3] = t1;
      } else {
        t1 = $[3];
      }
      collectionData = t1;
    }
    if (!collectionData && collectionId) {
      let t1;
      if ($[4] !== collectionId || $[5] !== collectionsData) {
        t1 = collectionsData.get(normalizeApiEndpoint(collectionId));
        $[4] = collectionId;
        $[5] = collectionsData;
        $[6] = t1;
      } else {
        t1 = $[6];
      }
      collectionData = t1;
    }
    if (!collectionData && collectionId) {
      if ($[7] !== collectionId || $[8] !== collectionsData) {
        for (const [key, value] of collectionsData.entries()) {
          if (key.includes(`/collections/${collectionId}`) || key.endsWith(`/${collectionId}`)) {
            collectionData = value;
            break;
          }
        }
        $[7] = collectionId;
        $[8] = collectionsData;
        $[9] = collectionData;
      } else {
        collectionData = $[9];
      }
    }
    if (collectionData && collectionData.length > 0) {
      headingApiData = collectionData[0] as unknown as Record<string, unknown>;
    }
  }
  let heroRowIndex;
  let t1;
  if ($[10] !== pageData.sections) {
    const sectionRowCounts = pageData.sections.map(_temp);
    heroRowIndex = firstContentRowIndex(pageData.sections);
    t1 = sectionRowCounts.reduce((acc, count, idx) => {
      acc.push(idx === 0 ? 0 : acc[idx - 1] + sectionRowCounts[idx - 1]);
      return acc;
    }, []);
    $[10] = pageData.sections;
    $[11] = heroRowIndex;
    $[12] = t1;
  } else {
    heroRowIndex = $[11];
    t1 = $[12];
  }
  const cumulativeRowCounts = t1;
  let t2;
  if ($[13] !== doubleApiData || $[14] !== headingApiData || $[15] !== pageBindingsData || $[16] !== pageData.pageHeading || $[17] !== pageData.sections) {
    t2 = pageData.pageHeading?.enabled && <PageHeader heading={pageData.pageHeading} apiData={headingApiData} pageBindingsData={pageBindingsData} doubleApiData={doubleApiData} sections={pageData.sections} />;
    $[13] = doubleApiData;
    $[14] = headingApiData;
    $[15] = pageBindingsData;
    $[16] = pageData.pageHeading;
    $[17] = pageData.sections;
    $[18] = t2;
  } else {
    t2 = $[18];
  }
  let t3;
  if ($[19] !== collectionsData || $[20] !== cumulativeRowCounts || $[21] !== dynamicTabsMap || $[22] !== heroRowIndex || $[23] !== includeSectionApiData || $[24] !== includeSectionsData || $[25] !== pageData.sections) {
    let t4;
    if ($[27] !== collectionsData || $[28] !== cumulativeRowCounts || $[29] !== dynamicTabsMap || $[30] !== heroRowIndex || $[31] !== includeSectionApiData || $[32] !== includeSectionsData) {
      t4 = (section, index) => <SectionRenderer key={section.id} section={section} collectionsData={collectionsData} dynamicTabsMap={dynamicTabsMap} includeSectionsData={includeSectionsData} includeSectionApiData={includeSectionApiData} heroRowIndex={heroRowIndex} globalRowStartIndex={cumulativeRowCounts[index] || 0} />;
      $[27] = collectionsData;
      $[28] = cumulativeRowCounts;
      $[29] = dynamicTabsMap;
      $[30] = heroRowIndex;
      $[31] = includeSectionApiData;
      $[32] = includeSectionsData;
      $[33] = t4;
    } else {
      t4 = $[33];
    }
    t3 = pageData.sections.map(t4);
    $[19] = collectionsData;
    $[20] = cumulativeRowCounts;
    $[21] = dynamicTabsMap;
    $[22] = heroRowIndex;
    $[23] = includeSectionApiData;
    $[24] = includeSectionsData;
    $[25] = pageData.sections;
    $[26] = t3;
  } else {
    t3 = $[26];
  }
  let t4;
  if ($[34] !== t2 || $[35] !== t3) {
    t4 = <div className="page-content border-[0.1px] border-DCDCDC">{t2}{t3}</div>;
    $[34] = t2;
    $[35] = t3;
    $[36] = t4;
  } else {
    t4 = $[36];
  }
  return t4;
}
function _temp(s) {
  return s.rows?.length || 0;
}